import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiSave, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PanelChart from './PanelChart';
import { buildMockData } from './mockPreviewData';
import { parseSelectColumns } from './parseSelectColumns';
import './PanelEditor.css';

/**
 * 쿼리 템플릿 생성/편집 모달.
 *
 * - 패널과 무관한 "재사용 가능한 (chartType, dbType, sql, x/y) 구성".
 * - builtin 템플릿은 수정 불가 (readOnly).
 * - 저장 payload 는 queryTemplateStore.create/update 시그니처와 동일.
 */

const getChartTypes = (t) => [
  { value: 'line', label: t('monitoring:qte.chart.line') },
  { value: 'vbar', label: t('monitoring:qte.chart.vbar') },
  { value: 'hbar', label: t('monitoring:qte.chart.hbar') },
  { value: 'grid', label: t('monitoring:qte.chart.grid') },
];

const getDbTypeOptions = (t) => [
  { value: 'any',        label: t('monitoring:qte.anyDbms') },
  { value: 'oracle',     label: 'Oracle' },
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'mysql',      label: 'MySQL' },
  { value: 'tibero',     label: 'Tibero' },
  { value: 'sqlite',     label: 'SQLite' },
];

const DEFAULT_DRAFT = {
  name: '',
  description: '',
  chartType: 'line',
  dbType: 'oracle',
  sql: '',
  xField: 't',
  yFields: [],
};

const QueryTemplateEditor = ({ open, template, onClose, onSave, onDelete }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraft(template ? { ...DEFAULT_DRAFT, ...template } : { ...DEFAULT_DRAFT });
  }, [open, template]);

  const previewData = useMemo(() => draft ? buildMockData(draft) : [], [draft]);

  if (!open || !draft) return null;

  const readOnly = !!(template && template.builtin);
  const isEdit = !!template?.id;

  const updateField = (k, v) => setDraft({ ...draft, [k]: v });
  const yFieldsText = (draft.yFields || []).join(', ');

  const handleSqlBlur = () => {
    if (readOnly) return;
    const parsed = parseSelectColumns(draft.sql);
    if (!parsed.xField && parsed.yFields.length === 0) return;
    setDraft(prev => ({
      ...prev,
      xField: parsed.xField || prev.xField,
      yFields: parsed.yFields.length > 0 ? parsed.yFields : prev.yFields,
    }));
  };

  const handleSave = () => {
    if (readOnly) return;
    const name = String(draft.name || '').trim();
    if (!name) { toast.error(t('monitoring:qte.nameRequired')); return; }
    if (!String(draft.sql || '').trim()) { toast.error(t('monitoring:qte.sqlRequired')); return; }
    if (!draft.xField) { toast.error(t('monitoring:qte.xRequired')); return; }
    const yFields = yFieldsText.split(',').map(s => s.trim()).filter(Boolean);
    if (yFields.length === 0) { toast.error(t('monitoring:qte.yRequired')); return; }
    onSave({
      name,
      description: draft.description || '',
      chartType: draft.chartType,
      dbType: draft.dbType || 'any',
      sql: draft.sql || '',
      xField: draft.xField,
      yFields,
    });
  };

  return (
    <div className="panel-editor-overlay" onClick={onClose}>
      <div className="app-modal panel-editor panel-editor-wide" onClick={(e) => e.stopPropagation()} data-app-modal>
        <div className="app-modal-header panel-editor-header">
          <h3 className="app-modal-title">
            <FiFileText size={14} />
            {readOnly ? t('monitoring:qte.titleReadonly') : (isEdit ? t('monitoring:qte.title') : t('monitoring:qte.new'))}
          </h3>
          <button className="app-modal-close panel-editor-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="panel-editor-body panel-editor-split">
          {/* 좌측: 입력 폼 */}
          <div className="panel-editor-form">
            <label className="panel-editor-label">
              <span className="panel-editor-label-text">{t('monitoring:qte.name')}<span className="panel-editor-required">*</span></span>
              <input
                className="panel-editor-input"
                type="text"
                value={draft.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder={t('monitoring:qte.ph.desc')}
                disabled={readOnly}
                maxLength={80}
              />
            </label>
            <label className="panel-editor-label">{t('monitoring:qte.descOptional')}
              <input
                className="panel-editor-input"
                type="text"
                value={draft.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder={t('monitoring:qte.descPlaceholder')}
                disabled={readOnly}
                maxLength={200}
              />
            </label>
            <div className="panel-editor-row">
              <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:qte.chartType')}
                <select
                  className="panel-editor-input"
                  value={draft.chartType}
                  onChange={(e) => updateField('chartType', e.target.value)}
                  disabled={readOnly}
                >
                  {getChartTypes(t).map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                </select>
              </label>
              <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:qte.targetDbms')}
                <select
                  className="panel-editor-input"
                  value={draft.dbType || 'any'}
                  onChange={(e) => updateField('dbType', e.target.value)}
                  disabled={readOnly}
                >
                  {getDbTypeOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            </div>
            <small className="panel-editor-hint">
              {t('monitoring:qte.dbmsNote')}
            </small>

            <label className="panel-editor-label">
              <span className="panel-editor-label-text">SQL<span className="panel-editor-required">*</span></span>
              <span className="panel-editor-hint-inline">{t('monitoring:qte.bindPrefix')} <code>:minutes</code>, <code>:hours</code> {t('monitoring:qte.availableSuffix')}</span>
              <textarea
                className="panel-editor-textarea"
                rows={8}
                value={draft.sql || ''}
                onChange={(e) => updateField('sql', e.target.value)}
                onBlur={handleSqlBlur}
                disabled={readOnly}
                placeholder={t('monitoring:ph.sql')}
              />
              <small className="panel-editor-hint">
                {t('monitoring:qte.sqlNote')}
              </small>
            </label>

            <div className="panel-editor-row">
              <label className="panel-editor-label" style={{ flex: 1 }}>
                <span className="panel-editor-label-text">{t('monitoring:qte.xLabel')}<span className="panel-editor-required">*</span></span>
                <input
                  className="panel-editor-input"
                  type="text"
                  value={draft.xField || ''}
                  onChange={(e) => updateField('xField', e.target.value)}
                  placeholder={t('monitoring:qte.ph.x')}
                  disabled={readOnly}
                />
              </label>
              <label className="panel-editor-label" style={{ flex: 2 }}>
                <span className="panel-editor-label-text">{t('monitoring:qte.yLabel')}<span className="panel-editor-required">*</span></span>
                <input
                  className="panel-editor-input"
                  type="text"
                  value={yFieldsText}
                  onChange={(e) => setDraft({ ...draft, yFields: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                  placeholder={t('monitoring:qte.ph.y')}
                  disabled={readOnly}
                />
              </label>
            </div>
            {readOnly && (
              <div className="panel-editor-hint" style={{ color: '#92400e' }}>
                {t('monitoring:qte.builtinNote1')}
                {t('monitoring:qte.builtinNote2')}
              </div>
            )}
          </div>

          {/* 우측: 미리보기 */}
          <div className="panel-editor-preview">
            <div className="panel-editor-preview-header">
              <span>{t('monitoring:qte.preview')}</span>
              <small>{t('monitoring:qte.sampleNote')}</small>
            </div>
            <div className="panel-editor-preview-body">
              <div className="panel-editor-preview-title">
                {draft.name?.trim() || t('monitoring:qte.noName')}
              </div>
              <PanelChart
                panel={{
                  chartType: draft.chartType,
                  xField: draft.xField || 'x',
                  yFields: (draft.yFields && draft.yFields.length > 0) ? draft.yFields : ['series_1'],
                }}
                data={previewData}
                height={240}
                isDarkMode={document.documentElement.getAttribute('data-theme') === 'dark'}
              />
              <div className="panel-editor-preview-meta">
                <div><strong>{t('monitoring:qte.chartColon')}</strong> {draft.chartType}</div>
                <div><strong>{t('monitoring:qte.dbmsColon')}</strong> {draft.dbType || 'any'}</div>
                {draft.description && (
                  <div><strong>{t('monitoring:qte.descColon')}</strong> {draft.description}</div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="panel-editor-footer">
          {isEdit && !readOnly && onDelete && (
            <button className="app-btn app-btn-danger panel-editor-btn panel-editor-btn-danger" onClick={() => onDelete(template)}>{t('monitoring:qte.delete')}</button>
          )}
          <div style={{ flex: 1 }} />
          <button className="app-btn panel-editor-btn" onClick={onClose}>{readOnly ? t('monitoring:qte.close') : t('monitoring:qte.cancel')}</button>
          {!readOnly && (
            <button className="app-btn app-btn-primary panel-editor-btn panel-editor-btn-primary" onClick={handleSave}>
              <FiSave size={14} />
              {t('monitoring:qte.save')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryTemplateEditor;
